import {
  Building2,
  Calendar,
  CheckCircle2,
  FolderKanban,
  Lightbulb,
  ListTodo,
  Package,
  Target,
  Zap,
} from "lucide-react";
import { Label } from "@/components/ui/label";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { useMeetingList } from "@/modules/meetings/hooks/useMeetings";
import { useTaskList } from "@/modules/tasks/hooks/useTasks";
import { useActionList } from "@/modules/actions/hooks/useActions";
import { useDecisionList } from "@/modules/decisions/hooks/useDecisions";
import { useIdeaList } from "@/modules/ideas/hooks/useIdeas";
import { useMilestoneList } from "@/modules/milestones/hooks/useMilestones";

type Field =
  | "companyId"
  | "productId"
  | "projectId"
  | "meetingId"
  | "taskId"
  | "actionId"
  | "decisionId"
  | "ideaId"
  | "milestoneId";

type Props = {
  companyId: string | null;
  productId: string | null;
  projectId: string | null;
  meetingId?: string | null;
  taskId?: string | null;
  actionId?: string | null;
  decisionId?: string | null;
  ideaId?: string | null;
  milestoneId?: string | null;
  onChange: (field: Field, value: string | null) => void;
  showMeeting?: boolean;
  showTask?: boolean;
  showAction?: boolean;
  showDecision?: boolean;
  showIdea?: boolean;
  showMilestone?: boolean;
  readOnly?: boolean;
};

export function AssociationFields({
  companyId,
  productId,
  projectId,
  meetingId = null,
  taskId = null,
  actionId = null,
  decisionId = null,
  ideaId = null,
  milestoneId = null,
  onChange,
  showMeeting = false,
  showTask = false,
  showAction = false,
  showDecision = false,
  showIdea = false,
  showMilestone = false,
  readOnly = false,
}: Props) {
  const { data: companiesResult } = useCompanyList({
    page: 1,
    limit: 200,
    operationalOnly: true,
  });
  const companies = companiesResult?.data ?? [];

  const { data: productsResult } = useProductList({
    page: 1,
    limit: 200,
    companyId: companyId ?? undefined,
  });
  const products = productsResult?.data ?? [];

  const { data: projectsResult } = useProjectList({
    page: 1,
    limit: 200,
    companyId: companyId ?? undefined,
    productId: productId ?? undefined,
  });
  const projects = projectsResult?.data ?? [];

  const { data: meetingsResult } = useMeetingList({
    page: 1,
    limit: 200,
  });
  const meetings = meetingsResult?.data ?? [];

  const { data: tasksResult } = useTaskList({
    page: 1,
    limit: 200,
  });
  const tasks = tasksResult?.data ?? [];

  const { data: actionsResult } = useActionList({
    page: 1,
    limit: 200,
  });
  const actions = actionsResult?.data ?? [];

  const { data: decisionsResult } = useDecisionList({ page: 1, limit: 100 });
  const decisions = decisionsResult?.data ?? [];

  const { data: ideasResult } = useIdeaList({ page: 1, limit: 100 });
  const ideas = ideasResult?.data ?? [];

  const { data: milestonesResult } = useMilestoneList({ page: 1, limit: 100 });
  const milestones = milestonesResult?.data ?? [];

  function handleCompanyChange(v: string) {
    const next = v || null;
    onChange("companyId", next);
    if (productId) onChange("productId", null);
    if (projectId) onChange("projectId", null);
  }

  function handleProductChange(v: string) {
    const next = v || null;
    onChange("productId", next);
    if (projectId) onChange("projectId", null);
  }

  const selectClass =
    "flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60";

  return (
    <div className="space-y-4">
      <div
        className="text-[12px] font-semibold uppercase tracking-wider text-muted-foreground"
      >
        Association
      </div>

      <div className="space-y-2">
        <Label htmlFor="assoc-company">Company</Label>
        <div className="relative">
          <Building2
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            strokeWidth={1.5}
          />
          <select
            id="assoc-company"
            value={companyId ?? ""}
            disabled={readOnly}
            onChange={(e) => handleCompanyChange(e.target.value)}
            className={selectClass}
          >
            <option value="">No company</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.code})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="assoc-product">Product</Label>
        <div className="relative">
          <Package
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            strokeWidth={1.5}
          />
          <select
            id="assoc-product"
            value={productId ?? ""}
            disabled={readOnly || !companyId}
            onChange={(e) => handleProductChange(e.target.value)}
            className={selectClass}
          >
            <option value="">No product</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.code})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="assoc-project">Project</Label>
        <div className="relative">
          <FolderKanban
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            strokeWidth={1.5}
          />
          <select
            id="assoc-project"
            value={projectId ?? ""}
            disabled={readOnly || (!companyId && !productId)}
            onChange={(e) => onChange("projectId", e.target.value || null)}
            className={selectClass}
          >
            <option value="">No project</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.code})
              </option>
            ))}
          </select>
        </div>
      </div>

      {showMeeting && (
        <div className="space-y-2">
          <Label htmlFor="assoc-meeting">Meeting</Label>
          <div className="relative">
            <Calendar
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              strokeWidth={1.5}
            />
            <select
              id="assoc-meeting"
              value={meetingId ?? ""}
              disabled={readOnly}
              onChange={(e) => onChange("meetingId", e.target.value || null)}
              className={selectClass}
            >
              <option value="">No meeting</option>
              {meetings.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.code} — {m.title}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {showTask && (
        <div className="space-y-2">
          <Label htmlFor="assoc-task">Task</Label>
          <div className="relative">
            <ListTodo
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              strokeWidth={1.5}
            />
            <select
              id="assoc-task"
              value={taskId ?? ""}
              disabled={readOnly}
              onChange={(e) => onChange("taskId", e.target.value || null)}
              className={selectClass}
            >
              <option value="">No task</option>
              {tasks.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.code} — {t.title}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {showAction && (
        <div className="space-y-2">
          <Label htmlFor="assoc-action">Action</Label>
          <div className="relative">
            <Zap
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              strokeWidth={1.5}
            />
            <select
              id="assoc-action"
              value={actionId ?? ""}
              disabled={readOnly}
              onChange={(e) => onChange("actionId", e.target.value || null)}
              className={selectClass}
            >
              <option value="">No action</option>
              {actions.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} — {a.title}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {showDecision && (
        <div className="space-y-2">
          <Label htmlFor="assoc-decision">Decision</Label>
          <div className="relative">
            <CheckCircle2
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              strokeWidth={1.5}
            />
            <select
              id="assoc-decision"
              value={decisionId ?? ""}
              disabled={readOnly}
              onChange={(e) =>
                onChange("decisionId", e.target.value || null)
              }
              className={selectClass}
            >
              <option value="">No decision</option>
              {decisions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.code} — {d.title}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {showIdea && (
        <div className="space-y-2">
          <Label htmlFor="assoc-idea">Parent Idea</Label>
          <div className="relative">
            <Lightbulb
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              strokeWidth={1.5}
            />
            <select
              id="assoc-idea"
              value={ideaId ?? ""}
              disabled={readOnly}
              onChange={(e) => onChange("ideaId", e.target.value || null)}
              className={selectClass}
            >
              <option value="">No parent idea</option>
              {ideas.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.code} — {i.title}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {showMilestone && (
        <div className="space-y-2">
          <Label htmlFor="assoc-milestone">Milestone</Label>
          <div className="relative">
            <Target
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              strokeWidth={1.5}
            />
            <select
              id="assoc-milestone"
              value={milestoneId ?? ""}
              disabled={readOnly}
              onChange={(e) =>
                onChange("milestoneId", e.target.value || null)
              }
              className={selectClass}
            >
              <option value="">No milestone</option>
              {milestones.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.code} — {m.title}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
